import _jsx from "@babel/runtime/helpers/jsx";
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

/** * Handles React elements by checking if the type is a function (component).
 * If it is, it renders the component and pushes its output onto the stack.
 * If not, it pushes the children of the element onto the stack.
 * @param {ReactElement} element - The React element to handle.
 * @param {Array} stack - The stack to push elements onto.
 */
const handleReactElement = (element, stack) => {
  if (typeof element.type === 'function') {
    // Render the component and push its output
    stack.push(element.type(element.props));
  } else {
    stack.push(element.props.children);
  }
};

/** * Handles array nodes by pushing their elements onto the stack in reverse order.
 * This ensures that the first element of the array is processed first.
 * @param {Array} arrayNode - The array node to handle.
 * @param {Array} stack - The stack to push elements onto.
 */
const handleArrayNode = (arrayNode, stack) => {
  // Push array elements in reverse to maintain order
  for (let i = arrayNode.length - 1; i >= 0; i--) {
    stack.push(arrayNode[i]);
  }
};

/** * Extracts text from a React node, handling strings, numbers, arrays, and React elements.
 * It replaces '---' with 'No change' and trims whitespace.
 * @param {ReactNode} node - The React node to extract text from.
 * @returns {string} - The extracted text, or 'No change' if the node is empty.
 */
const extractTextFromReactNode = node => {
  if (!node) return 'No change';
  const stack = [node];
  const result = [];
  while (stack.length > 0) {
    const current = stack.pop();
    if (typeof current === 'string' || typeof current === 'number') {
      const text = current.toString().trim();
      result.push(text === '---' ? 'No change' : text);
    } else if (Array.isArray(current)) {
      handleArrayNode(current, stack);
    } else if (/*#__PURE__*/React.isValidElement(current)) {
      handleReactElement(current, stack);
    }
  }
  return result.join(' ') || '';
};

/** * Renders a header cell for the table.
 * It uses the title from the column props and adds accessibility attributes if needed.
 * @param {Object} column - The column props containing the title.
 * @param {boolean} crosstabAccessible - Whether to add accessibility features.
 * @returns {JSX.Element} - The rendered header cell.
 */
const renderHeaderCell = (_ref, crosstabAccessible) => {
  let {
    title
  } = _ref;
  return /*#__PURE__*/_jsx("th", {
    scope: "col",
    "aria-hidden": !title ? true : undefined,
    "aria-label": crosstabAccessible && title ? title : undefined
  }, title, title);
};

/** * Renders a data cell for the table.
 * It uses the value from the column props and adds accessibility attributes if needed.
 * It also handles row headers and applies appropriate styles.
 * @param {Object} param0 - The column props containing the value, labelPrefix, labelSuffix, and other attributes.
 * @param {number} index - The index of the column in the row.
 * @param {boolean} crosstabAccessible - Whether to add accessibility features.
 * @returns {JSX.Element} - The rendered data cell.
 */
const renderDataCell = (_ref2, index, crosstabAccessible) => {
  let {
    value,
    labelPrefix,
    labelSuffix,
    ...rest
  } = _ref2;
  const textValue = extractTextFromReactNode(value);
  const isRowHeader = index === 0 && crosstabAccessible;
  let ariaLabel;
  if (isRowHeader) {
    ariaLabel = textValue;
  } else if (labelPrefix && crosstabAccessible) {
    ariaLabel = `${labelPrefix}${labelSuffix ? ' - ' : ''}${labelSuffix}: ${textValue}`;
  }
  const CellTag = isRowHeader ? 'th' : 'td';
  const cellProps = {
    key: index,
    'aria-label': ariaLabel,
    ...rest
  };
  if (CellTag === 'th') {
    cellProps.scope = 'row';
    cellProps.style = {
      fontWeight: 'normal'
    };
  }
  return /*#__PURE__*/React.createElement(CellTag, cellProps, value);
};

/**
*   DataTable will accept a data prop that is an array of data objects and render a table with a data row for each item.
*   Columns are defined by passing <DataTable.Column> child components to the <DataTable>.
*   The <DataTable> components should have a title prop, a dataKey, and an optional render method.
*/
function DataTable(_ref3) {
  let {
    data,
    className,
    children,
    striped = false,
    bordered = false,
    hover = false,
    condensed = false,
    unstyled = false,
    crosstabAccessible = false,
    ...rest
  } = _ref3;
  const columns = React.Children.map(children, child => child.props);
  const rootClassName = clsx({
    table: true,
    'table-striped': striped,
    'table-bordered': bordered,
    'table-hover': hover,
    'table-condensed': condensed,
    'table-unstyled': unstyled
  }, className);
  const renderDataCells = (row, rowIndex) => {
    const rowHeader = columns[0].render ? columns[0].render({
      value: row[columns[0].dataKey],
      columns,
      row,
      rowIndex,
      columnIndex: 0
    }) : row[columns[0].dataKey];
    return columns.map((_ref4, columnIndex) => {
      let {
        render,
        dataKey,
        title,
        ...columnRest
      } = _ref4;
      let value = row[dataKey];
      if (typeof render === 'function') {
        value = render({
          value: row[dataKey],
          columns,
          row,
          rowIndex,
          columnIndex
        });
      }
      return renderDataCell({
        value,
        labelPrefix: rowHeader,
        labelSuffix: title || '',
        // fallback if no thead
        ...columnRest
      }, columnIndex, crosstabAccessible);
    });
  };
  const renderDataRows = () => data.map((row, rowIndex) => /*#__PURE__*/_jsx("tr", {}, rowIndex, renderDataCells(row, rowIndex)));
  const renderThead = () => columns.find(column => !!column.title) && /*#__PURE__*/_jsx("thead", {}, void 0, /*#__PURE__*/_jsx("tr", {}, void 0, columns.map(col => renderHeaderCell(col, crosstabAccessible))));
  return /*#__PURE__*/React.createElement("table", Object.assign({
    "data-cmp": "dataTable"
  }, rest, {
    className: rootClassName
  }), renderThead(), /*#__PURE__*/_jsx("tbody", {}, void 0, renderDataRows()));
}
DataTable.Column = props => {
  // eslint-disable-next-line no-unused-vars
  const {
    dataKey,
    ...rest
  } = props;
  return /*#__PURE__*/React.createElement("div", rest);
};
export default DataTable;